import { Field, FieldError } from '@playbooks/ui/components/field'
import { InputGroupAddon, InputGroupInput } from '@playbooks/ui/components/input-group'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useEffect, useRef } from 'react'
import InputGroupWrapper from '../../../components/ui/InputGroupeWrapper'

interface NumberInputBaseProps {
  id: string
  label: string
  value: string | number
  onChange: (value: number | undefined) => void
  min?: number
  max?: number
  step?: number
  required?: boolean
  invalid?: boolean
  errorMessage?: string
  suffix?: React.ReactNode
  prefix?: React.ReactNode
  placeholder?: string
  formatValue?: (value: string | number) => string
  parseValue?: (value: string) => string
  testId?: string
}

/**
 * Base number input component with increment/decrement buttons
 * Can be extended for specific number types (percentage, currency, etc.)
 */
export default function NumberInputBase({
  id,
  value,
  onChange,
  min = -Infinity,
  max = Infinity,
  step = 1,
  required = false,
  invalid = false,
  errorMessage,
  suffix,
  prefix,
  placeholder = '',
  formatValue,
  parseValue,
  testId,
}: NumberInputBaseProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const format = (val: string | number) => (formatValue ? formatValue(val) : String(val ?? ''))

  // Sync input with external value changes (e.g. increment/decrement, external updates)
  // Skip when the input is focused — the user is actively typing
  useEffect(() => {
    if (inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.value = format(value ?? '')
    }
  }, [value, formatValue])

  // Keep the input value within the specified min and max range
  function clamp(val: number) {
    if (val >= max) {
      return max
    }

    if (val <= min) {
      return min
    }

    return val
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.target
    const inputValue = input.value
    const cursorPosition = input.selectionStart

    // Parse value if custom parser provided (e.g., remove thousand separators)
    const parsedValue = parseValue ? parseValue(inputValue) : inputValue

    // Only allow valid numeric input: empty string, numbers, decimals, and negative sign
    // Block commas and any letters or invalid characters
    if (parsedValue !== '' && !/^-?\d*\.?\d*$/.test(parsedValue)) {
      // Reject the input — restore the previous DOM value without re-render
      input.value = format(value ?? '')
      return
    }

    let newDisplayValue: string
    if (parsedValue !== '' && !parsedValue.endsWith('.') && !parsedValue.endsWith('-')) {
      // Clamp and re-format so DOM value stays in sync with what the prop-change would produce
      const clampedNum = clamp(Number(parsedValue))
      newDisplayValue = format(clampedNum)
    } else {
      // Keep intermediate states like "123." or "-" as-is
      newDisplayValue = parsedValue
    }

    // Update the DOM value directly — no React re-render, cursor stays put
    input.value = newDisplayValue

    // Restore cursor adjusted for any length change caused by formatting (e.g. thousand separators)
    if (cursorPosition !== null) {
      const newCursor = cursorPosition + (newDisplayValue.length - inputValue.length)
      input.setSelectionRange(newCursor, newCursor)
    }

    const num = Number(parsedValue)
    if (!isNaN(num) && parsedValue !== '' && !parsedValue.endsWith('.')) {
      onChange(clamp(num))
    } else if (parsedValue === '') {
      onChange(undefined)
    }
  }

  function handleDecrement() {
    const currentValue = value ?? 0
    const newValue = clamp(+currentValue - step)
    onChange(newValue)
  }

  function handleIncrement() {
    const currentValue = value ?? 0
    const newValue = clamp(+currentValue + step)
    onChange(newValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      handleIncrement()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      handleDecrement()
    }
  }

  return (
    <Field data-invalid={invalid} className="relative gap-2">
      <InputGroupWrapper>
        {prefix && <InputGroupAddon align="inline-start">{prefix}</InputGroupAddon>}
        <InputGroupInput
          data-testid={testId}
          id={id}
          type="text"
          inputMode="numeric"
          placeholder={placeholder}
          defaultValue={format(value ?? '')}
          ref={inputRef}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          required={required}
        />
        {suffix && <InputGroupAddon align="inline-end">{suffix}</InputGroupAddon>}
        <InputGroupAddon align="inline-end" className="flex flex-col gap-0">
          <button tabIndex={-1} type="button" onClick={handleIncrement}>
            <ChevronUp size={16} />
          </button>
          <button tabIndex={-1} type="button" onClick={handleDecrement}>
            <ChevronDown size={16} />
          </button>
        </InputGroupAddon>
      </InputGroupWrapper>
      {invalid && errorMessage && <FieldError className="absolute -bottom-4 text-xs">{errorMessage}</FieldError>}
    </Field>
  )
}
